import React, { useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom';
import { HiOutlineLockClosed, HiOutlineEmojiSad, HiOutlineCreditCard, HiOutlineShoppingBag, HiOutlineSparkles, HiCheckCircle, HiExclamationCircle, HiOutlineServer } from 'react-icons/hi';
import { FaGoogle } from 'react-icons/fa';
import SettingsService from '../service/SettingsService';
import AuthenticationService from '../service/AuthenticationService';
import '../css/admin.css'
import '../css/edit-item.css'
import Skeleton from '../components/Skeleton';
import EmptyState from '../components/EmptyState';
import Toast from '../components/Toast';
import brand from '../brand'
import { forgetGoogleClientId } from '../components/GoogleSignInButton'

// UPI and card are both paid in Razorpay Checkout and share its keys
const UPI_ENABLED = 'razorpay_upi_enabled'
const CARD_ENABLED = 'razorpay_payment_enabled'
const RZP_KEY_ID = 'razorpay_key_id'
const RZP_KEY_SECRET = 'razorpay_key_secret'
const GOOGLE_ENABLED = 'google_sign_in_enabled'
const GOOGLE_CLIENT_ID = 'google_client_id'
// AI Help for shoppers (Claude or Groq): each provider keeps its own key (a secret, shown only as ••••1234) and model
const AI_ENABLED = 'ai_help_enabled'
const AI_KEY = 'ai_anthropic_api_key'
const AI_LIMIT = 'ai_daily_limit'
const AI_MODEL = 'ai_model'
const AI_PROVIDER = 'ai_provider'
const AI_GROQ_KEY = 'ai_groq_api_key'
const AI_GROQ_MODEL = 'ai_groq_model'
const AI_PROVIDERS = {
    anthropic: {
        label: 'Claude', name: 'Anthropic', keyField: 'newKey', modelField: 'model', savedKey: AI_KEY,
        placeholder: 'sk-ant-…', site: 'console.anthropic.com', console: 'console.anthropic.com → API keys.',
        models: [
            { value: 'claude-haiku-5-5', label: 'Haiku 5.5' },
            { value: 'claude-sonnet-5-5', label: 'Sonnet 5.5' },
        ],
        modelHint: 'Haiku is fast and low cost (recommended); Sonnet is smarter and costs more.'
    },
    groq: {
        label: 'Groq · free tier', name: 'Groq', keyField: 'newGroqKey', modelField: 'groqModel', savedKey: AI_GROQ_KEY,
        placeholder: 'gsk_…', site: 'console.groq.com', console: 'console.groq.com → API Keys (free, no card needed).',
        models: [
            { value: 'openai/gpt-oss-120b', label: 'GPT-OSS 120B' },
            { value: 'llama-3.3-70b-versatile', label: 'Llama 3.3 70B' },
            { value: 'openai/gpt-oss-20b', label: 'GPT-OSS 20B' },
        ],
        modelHint: 'GPT-OSS 120B uses the shop tools best (recommended); GPT-OSS 20B is the fastest.'
    }
}
const aiProvider = (value) => (value === 'groq' ? 'groq' : 'anthropic')
// Services page: addresses to check, optional Render / Mailtrap access (secrets, shown as ••••1234), USD→₹ rate
const MON_SITE = 'monitor_site_url'
const MON_API = 'monitor_api_url'
const MON_DOMAIN = 'monitor_domain'
const MON_RENDER_KEY = 'monitor_render_api_key'
const MON_RENDER_SERVICE = 'monitor_render_service'
const MON_MAILTRAP_ID = 'monitor_mailtrap_account_id'
const MON_MAILTRAP_TOKEN = 'monitor_mailtrap_token'
const MON_USD_INR = 'monitor_usd_inr'
const HTTPS_URL_PATTERN = /^https:\/\/[A-Za-z0-9.-]+(:[0-9]{2,5})?(\/\S*)?$/
const DOMAIN_PATTERN = /^([a-z0-9-]+\.)+[a-z]{2,}$/
// seller details printed on invoices and delivery slips
const SHOP_FIELDS = [
    { key: 'shop_name', label: 'Shop name', placeholder: brand.fullName, max: 100, autoComplete: 'organization' },
    { key: 'shop_address', label: 'Address', placeholder: 'Street, city, state, PIN', max: 300, autoComplete: 'street-address' },
    { key: 'shop_phone', label: 'Phone', placeholder: '+91 74188 87311', max: 20, autoComplete: 'tel' },
    { key: 'shop_email', label: 'Email', placeholder: brand.contact.email || 'hello@yourshop.in', max: 100, autoComplete: 'email' }
]
const PHONE_PATTERN = /^[+0-9 ()-]{6,20}$/
const EMAIL_PATTERN = /^[^\s@]{1,64}@[^\s@]+\.[^\s@]{2,}$/
const RZP_KEY_ID_PATTERN = /^rzp_(test|live)_[A-Za-z0-9]{6,40}$/
const GOOGLE_CLIENT_ID_PATTERN = /^[0-9]{6,30}-[a-z0-9]{10,64}\.apps\.googleusercontent\.com$/
const LEAVE_MESSAGE = 'You have unsaved changes in Settings. Leave without saving?'

// Editable values per section, from the saved settings. Masked secrets (••••1234) are never put in a field:
// the secret / client id fields stay empty unless the admin types a new one.
const formsFrom = (saved) => ({
    payments: {
        upiEnabled: saved[UPI_ENABLED] === 'true',
        cardEnabled: saved[CARD_ENABLED] === 'true',
        keyId: saved[RZP_KEY_ID] || '',
        newSecret: ''
    },
    google: {
        enabled: saved[GOOGLE_ENABLED] === 'true',
        newClientId: ''
    },
    shop: Object.fromEntries(SHOP_FIELDS.map(f => [f.key, saved[f.key] || ''])),
    ai: {
        enabled: saved[AI_ENABLED] === 'true',
        provider: aiProvider(saved[AI_PROVIDER]),
        newKey: '',
        newGroqKey: '',
        limit: saved[AI_LIMIT] || '30',
        model: saved[AI_MODEL] || AI_PROVIDERS.anthropic.models[0].value,
        groqModel: saved[AI_GROQ_MODEL] || AI_PROVIDERS.groq.models[0].value
    },
    monitor: {
        siteUrl: saved[MON_SITE] || '',
        apiUrl: saved[MON_API] || '',
        domain: saved[MON_DOMAIN] || '',
        newRenderKey: '',
        renderService: saved[MON_RENDER_SERVICE] || '',
        mailtrapId: saved[MON_MAILTRAP_ID] || '',
        newMailtrapToken: '',
        usdInr: saved[MON_USD_INR] || '84'
    }
})

const paymentsStatus = (saved) => {
    const on = [saved[UPI_ENABLED] === 'true' && 'UPI on', saved[CARD_ENABLED] === 'true' && 'Card on'].filter(Boolean)
    return on.length ? on.join(' · ') : 'Off · WhatsApp orders'
}

// One entry per section: the side nav, the panel header and what each Save sends come from here
const SECTIONS = [
    {
        id: 'payments', label: 'Payments', icon: <HiOutlineCreditCard />,
        description: 'UPI and card payments via Razorpay.',
        status: paymentsStatus
    },
    {
        id: 'google', label: 'Google sign-in', short: 'Google', icon: <FaGoogle />,
        description: 'Google button on login and sign-up.',
        status: (saved) => saved[GOOGLE_ENABLED] === 'true' ? 'On' : 'Off'
    },
    {
        id: 'shop', label: 'Shop details', short: 'Shop', icon: <HiOutlineShoppingBag />,
        description: 'Printed on invoices and delivery slips.',
        status: (saved) => saved.shop_name ? (saved.shop_address ? saved.shop_name : `${saved.shop_name} · add address`) : 'Missing shop name'
    },
    {
        id: 'ai', label: 'AI Help', icon: <HiOutlineSparkles />,
        description: 'A chat assistant for shoppers.',
        status: (saved) => {
            const p = AI_PROVIDERS[aiProvider(saved[AI_PROVIDER])]
            if (saved[AI_ENABLED] === 'true') return `On · ${p.name} · ${saved[AI_LIMIT] || 30} a day`
            return saved[p.savedKey] ? 'Off · key saved' : 'Off'
        }
    },
    {
        id: 'monitor', label: 'Monitoring', icon: <HiOutlineServer />,
        description: 'What the Services page checks.',
        status: (saved) => {
            const extra = [saved[MON_RENDER_KEY] && 'Render', saved[MON_MAILTRAP_TOKEN] && 'Mailtrap'].filter(Boolean)
            return extra.length ? `Free checks · ${extra.join(' + ')}` : 'Free checks only'
        }
    }
]
const SECTION_IDS = SECTIONS.map(s => s.id)

// ---------- validation and save payloads, per section ----------
const validate = {
    payments: (form, saved) => {
        const errors = {}
        const keyId = form.keyId.trim()
        const anyOn = form.upiEnabled || form.cardEnabled
        if (keyId && !RZP_KEY_ID_PATTERN.test(keyId)) errors.keyId = 'Enter the key ID from Razorpay, e.g. rzp_live_ABC123…'
        else if (anyOn && !keyId) errors.keyId = 'A key ID is required while UPI or card payments are on.'
        if (anyOn && !form.newSecret.trim() && !saved[RZP_KEY_SECRET]) errors.secret = 'A key secret is required while UPI or card payments are on.'
        return errors
    },
    google: (form, saved) => {
        const errors = {}
        const id = form.newClientId.trim()
        if (id && !GOOGLE_CLIENT_ID_PATTERN.test(id)) errors.clientId = 'Paste the Client ID (…apps.googleusercontent.com), not the client secret.'
        else if (form.enabled && !id && !saved[GOOGLE_CLIENT_ID]) errors.clientId = 'A Client ID is required while Google sign-in is on.'
        return errors
    },
    shop: (form) => {
        const errors = {}
        const phone = form.shop_phone.trim()
        const email = form.shop_email.trim()
        if (!form.shop_name.trim()) errors.shop_name = 'The shop name is printed on every invoice.'
        if (phone && !PHONE_PATTERN.test(phone)) errors.shop_phone = 'Enter a valid phone number.'
        if (email && !EMAIL_PATTERN.test(email)) errors.shop_email = 'Enter a valid email address.'
        return errors
    },
    ai: (form, saved) => {
        const errors = {}
        const p = AI_PROVIDERS[form.provider]
        const key = form[p.keyField].trim()
        const limit = Number(form.limit)
        if (key && (key.length < 20 || /\s/.test(key))) errors.key = `Paste the whole API key from ${p.site}.`
        else if (form.enabled && !key && !saved[p.savedKey]) errors.key = `A ${p.name} API key is required while AI Help is on.`
        if (!/^\d+$/.test(String(form.limit).trim()) || limit < 1 || limit > 500) errors.limit = 'Choose a number from 1 to 500.'
        return errors
    },
    monitor: (form) => {
        const errors = {}
        const url = (v) => !v.trim() || HTTPS_URL_PATTERN.test(v.trim())
        if (!url(form.siteUrl)) errors.siteUrl = 'Enter the full https:// address of the shop.'
        if (!url(form.apiUrl)) errors.apiUrl = 'Enter the full https:// address of the server.'
        if (form.domain.trim() && !DOMAIN_PATTERN.test(form.domain.trim())) errors.domain = 'Just the domain, e.g. mevislittlekingdom.in'
        const key = form.newRenderKey.trim()
        if (key && (key.length < 20 || /\s/.test(key))) errors.renderKey = 'Paste the whole key from Render.'
        if (form.renderService.trim() && !/^[A-Za-z0-9._-]{1,60}$/.test(form.renderService.trim())) errors.renderService = 'The service name, e.g. mevis-java'
        if (form.mailtrapId.trim() && !/^\d{1,15}$/.test(form.mailtrapId.trim())) errors.mailtrapId = 'The account ID is a number.'
        const token = form.newMailtrapToken.trim()
        if (token && (token.length < 20 || /\s/.test(token))) errors.mailtrapToken = 'Paste the whole token from Mailtrap.'
        const rate = Number(form.usdInr)
        if (!(rate >= 1 && rate <= 500)) errors.usdInr = 'Enter a rate from 1 to 500.'
        return errors
    }
}

const payload = {
    payments: (form) => {
        const changes = {
            [UPI_ENABLED]: String(form.upiEnabled),
            [CARD_ENABLED]: String(form.cardEnabled),
            [RZP_KEY_ID]: form.keyId.trim()
        }
        // only send the secret when a new one was typed; otherwise the stored secret is kept
        if (form.newSecret.trim()) changes[RZP_KEY_SECRET] = form.newSecret.trim()
        return changes
    },
    google: (form) => {
        const changes = { [GOOGLE_ENABLED]: String(form.enabled) }
        if (form.newClientId.trim()) changes[GOOGLE_CLIENT_ID] = form.newClientId.trim()
        return changes
    },
    shop: (form) => Object.fromEntries(SHOP_FIELDS.map(f => [f.key, form[f.key].trim()])),
    ai: (form) => {
        const changes = {
            [AI_ENABLED]: String(form.enabled), [AI_PROVIDER]: form.provider, [AI_LIMIT]: String(Number(form.limit)),
            [AI_MODEL]: form.model, [AI_GROQ_MODEL]: form.groqModel
        }
        // only send a key when a new one was typed; otherwise the stored key is kept
        if (form.newKey.trim()) changes[AI_KEY] = form.newKey.trim()
        if (form.newGroqKey.trim()) changes[AI_GROQ_KEY] = form.newGroqKey.trim()
        return changes
    },
    monitor: (form) => {
        const changes = {
            [MON_SITE]: form.siteUrl.trim(), [MON_API]: form.apiUrl.trim(), [MON_DOMAIN]: form.domain.trim(),
            [MON_RENDER_SERVICE]: form.renderService.trim(), [MON_MAILTRAP_ID]: form.mailtrapId.trim(), [MON_USD_INR]: String(Number(form.usdInr))
        }
        // only send a secret when a new one was typed; otherwise the stored one is kept
        if (form.newRenderKey.trim()) changes[MON_RENDER_KEY] = form.newRenderKey.trim()
        if (form.newMailtrapToken.trim()) changes[MON_MAILTRAP_TOKEN] = form.newMailtrapToken.trim()
        return changes
    }
}

// ---------- section fields ----------
const Switch = ({ checked, onChange, on, off }) => (
    <label className='mv-switch'>
        <input type='checkbox' checked={checked} onChange={e => onChange(e.target.checked)} />
        <span className='mv-switch-track' aria-hidden='true'><span className='mv-switch-thumb' /></span>
        <span className='mv-switch-label'>{checked ? on : off}</span>
    </label>
)

const PaymentsSection = ({ form, set, errors, saved }) => (
    <>
        <div className='mv-form-grid'>
            <div className='mv-field'>
                <Switch checked={form.upiEnabled} onChange={v => set({ upiEnabled: v }, ['keyId', 'secret'])}
                    on='UPI payments on' off='UPI payments off' />
                <span className='mv-field-hint'>Google Pay, PhonePe, Paytm or any UPI app on phones; a QR code to scan on computers.</span>
            </div>

            <div className='mv-field'>
                <Switch checked={form.cardEnabled} onChange={v => set({ cardEnabled: v }, ['keyId', 'secret'])}
                    on='Card payments on' off='Card payments off' />
                <span className='mv-field-hint'>Credit and debit cards: Visa, Mastercard, RuPay.</span>
            </div>

            <div className='mv-field'>
                <label className='mv-label' htmlFor='rzp-key-id'>Key ID</label>
                <input id='rzp-key-id' className='mv-input' autoComplete='off' placeholder='rzp_live_…'
                    value={form.keyId} maxLength={60} aria-invalid={!!errors.keyId}
                    aria-describedby={errors.keyId ? 'rzp-key-id-error' : 'rzp-key-id-hint'}
                    onChange={e => set({ keyId: e.target.value }, ['keyId'])} />
                {errors.keyId ?
                    <span id='rzp-key-id-error' className='mv-field-error'>{errors.keyId}</span> :
                    <span id='rzp-key-id-hint' className='mv-field-hint'>
                        Razorpay dashboard → Account &amp; Settings → API keys. Use rzp_test_… keys to try it out.
                    </span>}
            </div>

            <div className='mv-field'>
                <label className='mv-label' htmlFor='rzp-key-secret'>Key secret</label>
                <input id='rzp-key-secret' className='mv-input' type='password' autoComplete='new-password'
                    placeholder={saved[RZP_KEY_SECRET] ? `Saved (${saved[RZP_KEY_SECRET]})` : 'Paste the key secret'}
                    value={form.newSecret} maxLength={200} aria-invalid={!!errors.secret}
                    aria-describedby={errors.secret ? 'rzp-key-secret-error' : 'rzp-key-secret-hint'}
                    onChange={e => set({ newSecret: e.target.value }, ['secret'])} />
                {errors.secret ?
                    <span id='rzp-key-secret-error' className='mv-field-error'>{errors.secret}</span> :
                    <span id='rzp-key-secret-hint' className='mv-field-hint'>
                        {saved[RZP_KEY_SECRET] ? 'Leave blank to keep the saved secret. It is never shown in full.' : 'Stored securely; only the last 4 characters are ever shown.'}
                    </span>}
            </div>
        </div>
        {/* below the fields, so every section's fields start at the same height */}
        <p className='mv-field-hint mv-settings-note'>
            Customers pay in Razorpay's secure checkout and every payment is confirmed automatically.
            With both off, customers are asked to order on WhatsApp.
        </p>
    </>
)

const GoogleSection = ({ form, set, errors, saved }) => (
    <div className='mv-form-grid'>
        <div className='mv-field'>
            <Switch checked={form.enabled} onChange={v => set({ enabled: v }, ['clientId'])}
                on='“Continue with Google” on' off='“Continue with Google” off' />
            <span className='mv-field-hint'>Shows Google's button on the login and sign-up pages. Customers can always use their email.</span>
        </div>

        <div className='mv-field'>
            <label className='mv-label' htmlFor='google-client-id'>Client ID</label>
            <input id='google-client-id' className='mv-input' type='password' autoComplete='off' spellCheck='false'
                placeholder={saved[GOOGLE_CLIENT_ID] ? `Saved (${saved[GOOGLE_CLIENT_ID]})` : 'Paste the Client ID'}
                value={form.newClientId} maxLength={120} aria-invalid={!!errors.clientId}
                aria-describedby={errors.clientId ? 'google-client-id-error' : 'google-client-id-hint'}
                onChange={e => set({ newClientId: e.target.value }, ['clientId'])} />
            {errors.clientId ?
                <span id='google-client-id-error' className='mv-field-error'>{errors.clientId}</span> :
                <span id='google-client-id-hint' className='mv-field-hint'>
                    {saved[GOOGLE_CLIENT_ID] ? 'Leave blank to keep the saved ID. ' : ''}
                    Google Cloud Console → APIs &amp; Services → Credentials → OAuth client ID (Web). Add this shop's web address under Authorized JavaScript origins.
                </span>}
        </div>
    </div>
)

const ShopSection = ({ form, set, errors }) => (
    <div className='mv-form-grid'>
        {SHOP_FIELDS.map(f => {
            const common = {
                id: f.key, placeholder: f.placeholder, autoComplete: f.autoComplete, maxLength: f.max, value: form[f.key],
                'aria-invalid': !!errors[f.key], 'aria-describedby': errors[f.key] ? f.key + '-error' : undefined,
                onChange: e => set({ [f.key]: e.target.value }, [f.key])
            }
            return (
                <div key={f.key} className='mv-field'>
                    <label className='mv-label' htmlFor={f.key}>{f.label}</label>
                    {f.key === 'shop_address' ?
                        <textarea className='mv-textarea' rows='3' {...common} /> :
                        <input className='mv-input' type={f.key === 'shop_email' ? 'email' : (f.key === 'shop_phone' ? 'tel' : 'text')} {...common} />}
                    {errors[f.key] && <span id={f.key + '-error'} className='mv-field-error'>{errors[f.key]}</span>}
                </div>
            )
        })}
    </div>
)

const AiSection = ({ form, set, errors, saved }) => {
    const [test, setTest] = useState(null) // { busy } | { ok, message }
    const p = AI_PROVIDERS[form.provider]
    const savedKey = saved[p.savedKey]
    const unsavedKey = !!form[p.keyField].trim()
    const unsavedProvider = form.provider !== aiProvider(saved[AI_PROVIDER])
    const runTest = () => {
        setTest({ busy: true })
        SettingsService.testAi()
            .then(res => setTest({ ok: !!res.data.ok, message: res.data.message }))
            .catch(error => setTest({ ok: false, message: error?.response?.data?.msg || 'Could not run the test. Please try again.' }))
    }
    return (
        <>
            <div className='mv-form-grid'>
                <div className='mv-field mv-field-full'>
                    <span className='mv-label' id='ai-provider-label'>Active provider</span>
                    <div className='mv-ai-provider' role='radiogroup' aria-labelledby='ai-provider-label'>
                        {Object.entries(AI_PROVIDERS).map(([value, opt]) =>
                            <label key={value} className={'mv-ai-provider-opt' + (form.provider === value ? ' active' : '')}>
                                <input type='radio' name='ai-provider' value={value} checked={form.provider === value}
                                    onChange={() => { set({ provider: value, newKey: '', newGroqKey: '' }, ['key']); setTest(null) }} />
                                {opt.label}
                            </label>)}
                    </div>
                    <span className='mv-field-hint'>
                        Only the selected provider answers shoppers; the other key stays saved for switching. {form.provider === 'groq'
                            ? 'Groq is free, with daily limits; when they run out, shoppers are asked to try later.'
                            : 'Claude is paid per message and the most reliable.'}
                    </span>
                </div>

                <div className='mv-field'>
                    <Switch checked={form.enabled} onChange={v => set({ enabled: v }, ['key'])}
                        on='AI Help on for shoppers' off='AI Help off' />
                    <span className='mv-field-hint'>Shows “AI Help” in the shopper menu and header. Turning it off hides it at once.</span>
                </div>

                <div className='mv-field'>
                    <label className='mv-label' htmlFor='ai-key'>{p.name} API key</label>
                    <input id='ai-key' key={form.provider} className='mv-input' type='password' autoComplete='new-password' spellCheck='false'
                        placeholder={savedKey ? `Saved (${savedKey})` : p.placeholder}
                        value={form[p.keyField]} maxLength={300} aria-invalid={!!errors.key}
                        aria-describedby={errors.key ? 'ai-key-error' : 'ai-key-hint'}
                        onChange={e => { set({ [p.keyField]: e.target.value }, ['key']); setTest(null) }} />
                    {errors.key ?
                        <span id='ai-key-error' className='mv-field-error'>{errors.key}</span> :
                        <span id='ai-key-hint' className='mv-field-hint'>
                            {savedKey ? 'Leave blank to keep the saved key. ' : ''}
                            {p.console} Stored securely; only the last 4 characters are ever shown.
                        </span>}
                    <div className='mv-ai-test'>
                        <button type='button' className='mv-btn mv-btn-secondary mv-btn-sm' onClick={runTest}
                            disabled={(test && test.busy) || unsavedKey || unsavedProvider || !savedKey}>
                            {test && test.busy ? 'Testing…' : 'Test connection'}
                        </button>
                        {(unsavedKey || unsavedProvider) &&
                            <span className='mv-field-hint'>{unsavedKey ? 'Save the new key first, then test it.' : 'Save to switch provider, then test it.'}</span>}
                        {test && !test.busy &&
                            <span className={'mv-ai-test-result ' + (test.ok ? 'is-ok' : 'is-bad')} role='status'>
                                {test.ok ? <HiCheckCircle aria-hidden='true' /> : <HiExclamationCircle aria-hidden='true' />} {test.message}
                            </span>}
                    </div>
                </div>

                <div className='mv-field'>
                    <label className='mv-label' htmlFor='ai-limit'>Daily limit per shopper</label>
                    <input id='ai-limit' className='mv-input' inputMode='numeric' autoComplete='off' value={form.limit}
                        maxLength={3} aria-invalid={!!errors.limit} aria-describedby={errors.limit ? 'ai-limit-error' : 'ai-limit-hint'}
                        onChange={e => set({ limit: e.target.value.replace(/\D/g, '') }, ['limit'])} />
                    {errors.limit ?
                        <span id='ai-limit-error' className='mv-field-error'>{errors.limit}</span> :
                        <span id='ai-limit-hint' className='mv-field-hint'>Caps cost and misuse. After the limit, shoppers are offered WhatsApp.</span>}
                </div>

                <div className='mv-field'>
                    <label className='mv-label' htmlFor='ai-model'>Model</label>
                    <select id='ai-model' className='mv-select' value={form[p.modelField]} onChange={e => set({ [p.modelField]: e.target.value })}>
                        {p.models.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                    <span className='mv-field-hint'>{p.modelHint}</span>
                </div>
            </div>
            <p className='mv-field-hint mv-settings-note'>
                AI Help only sees the signed-in shopper's own profile, cart, orders and the declaration. It changes the cart or
                profile only after the shopper taps Confirm, and it can never pay. Their questions and these details are sent to
                {' ' + p.name} to answer them. {form.provider === 'groq'
                    ? 'Check the free limits at console.groq.com → Settings → Limits.'
                    : 'Set a monthly spend limit in the Anthropic console.'}
            </p>
        </>
    )
}

// a text field with its error or hint, for the Monitoring section
const MonField = ({ id, label, hint, error, ...input }) => (
    <div className='mv-field'>
        <label className='mv-label' htmlFor={id}>{label}</label>
        <input id={id} className='mv-input' autoComplete='off' spellCheck='false' aria-invalid={!!error}
            aria-describedby={id + (error ? '-error' : '-hint')} {...input} />
        {error ? <span id={id + '-error'} className='mv-field-error'>{error}</span> :
            <span id={id + '-hint'} className='mv-field-hint'>{hint}</span>}
    </div>
)

const MonitorSection = ({ form, set, errors, saved }) => (
    <>
        <div className='mv-form-grid'>
            <MonField id='mon-site' label='Shop address' value={form.siteUrl} maxLength={200} placeholder='https://www.yourshop.in'
                error={errors.siteUrl} hint='Checked for being up, speed and its SSL certificate.'
                onChange={e => set({ siteUrl: e.target.value }, ['siteUrl'])} />
            <MonField id='mon-api' label='Server address' value={form.apiUrl} maxLength={200} placeholder='https://mevis-java.onrender.com'
                error={errors.apiUrl} hint='The Java server; its health check is called.'
                onChange={e => set({ apiUrl: e.target.value }, ['apiUrl'])} />
            <MonField id='mon-domain' label='Domain' value={form.domain} maxLength={100} placeholder='Taken from the shop address'
                error={errors.domain} hint='Its expiry date is read from the public domain registry.'
                onChange={e => set({ domain: e.target.value.toLowerCase() }, ['domain'])} />
            <MonField id='mon-rate' label='Dollar rate (₹ for $1)' value={form.usdInr} maxLength={6} inputMode='decimal'
                error={errors.usdInr} hint='Turns dollar plans (Render, AWS) into rupees.'
                onChange={e => set({ usdInr: e.target.value.replace(/[^0-9.]/g, '') }, ['usdInr'])} />
            <MonField id='mon-render-key' label='Render API key (optional)' type='password' value={form.newRenderKey} maxLength={200}
                placeholder={saved[MON_RENDER_KEY] ? `Saved (${saved[MON_RENDER_KEY]})` : 'rnd_…'} error={errors.renderKey}
                hint={(saved[MON_RENDER_KEY] ? 'Leave blank to keep the saved key. ' : '') + 'Render → Account settings → API keys. Adds plan and last deploy.'}
                onChange={e => set({ newRenderKey: e.target.value }, ['renderKey'])} />
            <MonField id='mon-render-service' label='Render service name' value={form.renderService} maxLength={60} placeholder='mevis-java'
                error={errors.renderService} hint='As shown in the Render dashboard.'
                onChange={e => set({ renderService: e.target.value }, ['renderService'])} />
            <MonField id='mon-mailtrap-id' label='Mailtrap account ID (optional)' value={form.mailtrapId} maxLength={15} inputMode='numeric'
                error={errors.mailtrapId} hint='Mailtrap → Settings → Account; the number in the address.'
                onChange={e => set({ mailtrapId: e.target.value.replace(/\D/g, '') }, ['mailtrapId'])} />
            <MonField id='mon-mailtrap-token' label='Mailtrap API token (optional)' type='password' value={form.newMailtrapToken} maxLength={200}
                placeholder={saved[MON_MAILTRAP_TOKEN] ? `Saved (${saved[MON_MAILTRAP_TOKEN]})` : 'Paste a token with account access'}
                error={errors.mailtrapToken}
                hint={(saved[MON_MAILTRAP_TOKEN] ? 'Leave blank to keep the saved token. ' : '') + 'Mailtrap → Settings → API Tokens. Adds the plan limit and emails sent.'}
                onChange={e => set({ newMailtrapToken: e.target.value }, ['mailtrapToken'])} />
        </div>
        <p className='mv-field-hint mv-settings-note'>
            Everything else on the Services page works without keys: the database size, S3 storage, emails, payments, Google
            sign-ins and AI messages are read from this shop's own records. Keys are stored securely and only the last 4
            characters are ever shown.
        </p>
    </>
)

const SECTION_FIELDS = { payments: PaymentsSection, google: GoogleSection, shop: ShopSection, ai: AiSection, monitor: MonitorSection }

// Admin-only app settings: a side nav (pills on phones) opens one section at a time, each saved on its own.
// The backend enforces ROLE_ADMIN; non-admins get 403 and see "Admins only".
const AppSettings = () => {
    const history = useHistory();
    const location = useLocation();
    const requested = new URLSearchParams(location.search).get('section')
    const current = SECTION_IDS.includes(requested) ? requested : SECTION_IDS[0]

    const [isLoading, setIsLoading] = useState(true)
    const [loadError, setLoadError] = useState(false)
    const [forbidden, setForbidden] = useState(false)
    // last values from the server (secrets masked) and the forms being edited, per section
    const [saved, setSaved] = useState({})
    const [forms, setForms] = useState(() => formsFrom({}))
    const [errors, setErrors] = useState({ payments: {}, google: {}, shop: {}, ai: {}, monitor: {} })
    const [saving, setSaving] = useState(null)
    const [saveError, setSaveError] = useState('')
    const [toast, setToast] = useState('')
    const headings = useRef({})
    const navRef = useRef(null)
    const switched = useRef(false)

    const savedForms = formsFrom(saved)
    const isDirty = (id) => JSON.stringify(forms[id]) !== JSON.stringify(savedForms[id])
    const anyDirty = !isLoading && SECTION_IDS.some(isDirty)

    const handleAuthError = (error) => {
        const status = error?.response?.status
        if (status === 401) {
            history.push('/login')
            return true
        }
        if (status === 403) {
            setForbidden(true)
            return true
        }
        return false
    }

    const loadSettings = () => {
        if (!AuthenticationService.canManageSettings()) {
            setForbidden(true)
            return
        }
        setIsLoading(true)
        setLoadError(false)
        SettingsService.getSettings().then(res => {
            setSaved(res.data)
            setForms(formsFrom(res.data))
            setIsLoading(false)
        }).catch(error => {
            setIsLoading(false)
            if (!handleAuthError(error)) setLoadError(true)
        });
    }

    useEffect(() => {
        loadSettings()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // unsaved edits: confirm before leaving Settings (switching sections stays on /settings and is never blocked)
    useEffect(() => {
        if (!anyDirty) return undefined
        const unblock = history.block(next => (next.pathname === location.pathname ? undefined : LEAVE_MESSAGE))
        const onBeforeUnload = (e) => {
            e.preventDefault()
            e.returnValue = LEAVE_MESSAGE
            return LEAVE_MESSAGE
        }
        window.addEventListener('beforeunload', onBeforeUnload)
        return () => {
            unblock()
            window.removeEventListener('beforeunload', onBeforeUnload)
        }
    }, [anyDirty, history, location.pathname])

    // after a switch: move focus to the new section; on phones centre its pill in the pill row
    // (only the row scrolls sideways; the page itself never moves)
    const centredOnLoad = useRef(false)
    useEffect(() => {
        if (isLoading) return
        const firstLoad = !centredOnLoad.current
        centredOnLoad.current = true
        if (!switched.current && !firstLoad) return
        const moveFocus = switched.current
        switched.current = false
        const title = headings.current[current]
        if (title && moveFocus) title.focus({ preventScroll: true })
        const nav = navRef.current
        const active = nav && nav.querySelector('[aria-current="page"]')
        if (active && window.matchMedia('(max-width: 767px)').matches && nav.scrollTo) {
            nav.scrollTo({ left: active.offsetLeft - (nav.clientWidth - active.offsetWidth) / 2, behavior: firstLoad ? 'auto' : 'smooth' })
        }
    }, [current, isLoading])

    const openSection = (id) => {
        if (id === current) return
        switched.current = true
        setSaveError('')
        history.replace({ pathname: location.pathname, search: '?section=' + id })
    }

    // update the open section's form and clear the errors of the fields that changed
    const setSection = (id) => (patch, clear = []) => {
        setForms(f => ({ ...f, [id]: { ...f[id], ...patch } }))
        if (clear.length) setErrors(e => ({ ...e, [id]: Object.fromEntries(Object.entries(e[id]).filter(([k]) => !clear.includes(k))) }))
        setSaveError('')
    }

    const discard = (id) => {
        setForms(f => ({ ...f, [id]: savedForms[id] }))
        setErrors(e => ({ ...e, [id]: {} }))
        setSaveError('')
    }

    const saveSection = (event, id) => {
        event.preventDefault()
        const sectionErrors = validate[id](forms[id], saved)
        setErrors(e => ({ ...e, [id]: sectionErrors }))
        setSaveError('')
        if (Object.keys(sectionErrors).length) return

        setSaving(id)
        SettingsService.updateSettings(payload[id](forms[id])).then(res => {
            // login/sign-up remember the Google client id; make them ask again
            if (id === 'google') forgetGoogleClientId()
            setSaved(res.data)
            // reset only this section; unsaved edits in the others stay
            setForms(f => ({ ...f, [id]: formsFrom(res.data)[id] }))
            setSaving(null)
            setToast(`${SECTIONS.find(s => s.id === id).label} saved`)
        }).catch(error => {
            setSaving(null)
            if (handleAuthError(error)) return
            setSaveError(error?.response?.data?.msg || 'We could not save these settings. Please try again.')
        });
    }

    if (forbidden) {
        return (
            <main className='mv-page'>
                <EmptyState icon={<HiOutlineLockClosed />} title='Admins only'
                    text='You need an admin account to change settings.'
                    actionLabel='Go to home' onAction={() => history.push('/')} />
            </main>
        )
    }

    if (loadError) {
        return (
            <main className='mv-page'>
                <EmptyState icon={<HiOutlineEmojiSad />} title='We could not load settings'
                    text='Please check your connection and try again.' actionLabel='Try again' onAction={loadSettings} />
            </main>
        )
    }

    return (
        <main className='mv-page mv-settings' aria-busy={isLoading}>
            <div className='mv-page-head'>
                <div>
                    <h1 className='mv-list-title'>Settings</h1>
                    <p className='mv-page-sub'>Payments, sign-in, shop details and AI Help for the whole shop.</p>
                </div>
            </div>

            <div className='mv-settings-layout'>
                <nav className='mv-settings-nav' aria-label='Settings sections' ref={navRef}>
                    {SECTIONS.map(s =>
                        <button key={s.id} type='button' onClick={() => openSection(s.id)}
                            className={'mv-settings-nav-item' + (s.id === current ? ' active' : '') + (!isLoading && isDirty(s.id) ? ' has-changes' : '')}
                            aria-current={s.id === current ? 'page' : undefined}>
                            <span className='mv-settings-nav-icon' aria-hidden='true'>{s.icon}</span>
                            <span className='mv-settings-nav-text'>
                                <span className='mv-settings-nav-label'>
                                    <span className='mv-settings-nav-full'>{s.label}</span>
                                    <span className='mv-settings-nav-short'>{s.short || s.label}</span>
                                </span>
                                <span className='mv-settings-nav-status'>
                                    {isLoading ? <Skeleton width='90px' height={12} /> : s.status(saved)}
                                </span>
                            </span>
                            {/* always rendered (only shown when there are edits) so the pill never changes width */}
                            <span className='mv-settings-nav-dot'>
                                {!isLoading && isDirty(s.id) && <span className='mv-sr-only'>Unsaved changes</span>}
                            </span>
                        </button>
                    )}
                </nav>

                {isLoading ?
                    <div className='mv-card mv-settings-panel'><Skeleton height={260} radius='12px' /></div> :
                    // every section sits in the same grid cell and only the open one is visible, so the panel is always
                    // as tall as the tallest section: switching never changes the page height, scroll or scrollbar
                    <div className='mv-card mv-settings-panel mv-settings-stack'>
                        {SECTIONS.map(section => {
                            const id = section.id
                            const open = id === current
                            const Fields = SECTION_FIELDS[id]
                            const dirty = isDirty(id)
                            const busy = saving === id
                            return (
                                <section key={id} className={'mv-settings-pane' + (open ? ' active' : '')}
                                    aria-labelledby={'settings-title-' + id} aria-hidden={open ? undefined : 'true'}
                                    inert={open ? undefined : ''}>
                                    <form onSubmit={e => saveSection(e, id)} noValidate>
                                        <header className='mv-settings-panel-head'>
                                            <span className='mv-settings-panel-icon' aria-hidden='true'>{section.icon}</span>
                                            <div>
                                                <h2 id={'settings-title-' + id} className='mv-card-title' tabIndex={-1}
                                                    ref={el => { headings.current[id] = el }}>{section.label}</h2>
                                                <p className='mv-settings-panel-sub'>{section.description}</p>
                                            </div>
                                        </header>

                                        <Fields form={forms[id]} set={setSection(id)} errors={errors[id]} saved={saved} />

                                        {open && saveError && <div className='mv-alert mv-alert-error' role='alert'>{saveError}</div>}

                                        <div className={'mv-settings-actions' + (dirty ? ' is-dirty' : '')}>
                                            {dirty && <span className='mv-settings-unsaved'>Unsaved changes</span>}
                                            {dirty &&
                                                <button type='button' className='mv-btn mv-btn-secondary' onClick={() => discard(id)} disabled={busy}>
                                                    Discard
                                                </button>}
                                            <button type='submit' className='mv-btn mv-btn-primary' disabled={busy || !dirty} aria-busy={busy}>
                                                {busy ? 'Saving…' : 'Save changes'}
                                            </button>
                                        </div>
                                    </form>
                                </section>
                            )
                        })}
                    </div>}
            </div>

            <Toast message={toast} onDone={() => setToast('')} />
        </main>
    )
}

export default AppSettings
